import { Injectable } from '@angular/core';
import { Item } from '@app/core/components/listbox/listbox.component';
import { DataTaskService } from '@app/core/services/data-task.service';
import { CustType } from './customer.service';

@Injectable({
    providedIn: 'root',
})
export class TypeaheadService {
    /**
     * List of the last search result of projects.
     */
    public suggestedProjects: Item[] = [];

    constructor(private datatask: DataTaskService) {}

    public lookUpInitiatedByPersonNames(params: InitiatedByPersonNameParams): Promise<InitiatedByPersonNameItem[]> {
        return this.datatask.Post(1125, params);
    }

    /**
     * @datatask 1974
     * @datataskName sp_ws_p2_project_search_list
     * @param parms
     * @returns promise containing list of prohects matching the params.
     */
    public async searchProjects(parms: SearchProjectParams): Promise<SearchProjectItem[]> {
        const data = await this.datatask.Post(1974, parms);

        return data;
    }

    /**
     * Searches for customers matching the given search criteria and returns a promise containing a list of account information for the customers.
     *
     * @param params search criteras for finding customer account info for.
     * @datatask 2090
     * @datataskName sp_ws_p2_findcustomers_acc_list
     * @returns promise containing list of customer account info for the customers matching the given search criterias.
     */
    public searchCustomerAccountInfo(params: SearchCustomerAccountInfoParams): Promise<CustomerAccountInfo[]> {
        return this.datatask.Post(2090, params);
    }

    /**
     * Searches for equipments matching the given equipment search query.
     *
     * @param equipmentName the text to use to search for equipments.
     * @datatask 1365
     * @datataskName sp_ws_p2_equipment_search_list
     * @returns a promise containing a list of equipments matching the given search query.
     */
    public searchEquipments(equipmentName: string): Promise<Item[]> {
        return this.datatask.Post(1365, { equipment_name: equipmentName });
    }

    /**
     * Searches for customer matching the given params.
     *
     * @param params the params to find matches in customer based of.
     * @datatask 56
     * @datataskName sp_ws_p2_findcustomers_list
     * @returns a promise containing list of customers matching the given params.
     */
    public searchCustomer(params: SearchCustomerParams): Promise<CustomerItem[]> {
        return this.datatask.Post(56, params);
    }

    /**
     * Searches for customer matching the given params.
     *
     * @param params the params to find matches in product based of.
     * @datatask 78
     * @datataskName sp_ws_p2_selectproduct_list
     * @returns a promise containing list of products matching the given params.
     */
    public lookUpProducts(params: SearchProductParams): Promise<Item[]> {
        return this.datatask.Post(78, params);
    }

    /**
     * Searches for custacts matching the given params.
     *
     * @param params the params to find matches in custacts based of.
     * @datatask 2724
     * @datataskName sp_ws_p2_custact_typeaheadsearch_list
     * @returns a promise containing list of custacts matching the given params.
     */
    public searchCustacts(params: SearchCustactParams): Promise<ItemCustact[]> {
        return this.datatask.Post(2724, params);
    }

    /**
     * Searches for postplaces using given params.
     *
     * @datatask 103
     * @datataskName sp_ws_p2_selectpostplace_list
     * @param params query for the search.
     * @returns promise containing list of postplaces.
     */
    public searchPostplaces(params: SearchPostPlaceParams): Promise<PostPlaceItem[]> {
        return this.datatask.Post(103, params);
    }

    /**
     * Retrieves list of stockbatches matching the given params.
     *
     * @datatask 3706
     * @datataskName sp_ws_p2_stockbatch_typeahead_list
     * @param params search params for list of stock batches to retrieve.
     * @returns promise containing list of stockbatches.
     */
    public searchStockBatches(params: SearchStockBatchParams): Promise<StockBatchItem[]> {
        return this.datatask.Post(3706, params);
    }

    /**
     * Searches for service objects.
     *
     * @datatask 1733
     * @datataskName sp_ws_p2_w_serviceobjects_list
     * @param params
     * @returns promise containing search results.
     */
    public async searchServiceObjects(params: SearchServiceObjectsParams): Promise<ServiceObjectItem[]> {
        return this.datatask.Post(1733, params);
    }

    /**
     * Searches for accounts.
     *
     * @datatask 1900
     * @datataskName sp_ws_p2_accounting_accountsearch_list
     * @param params
     * @returns promise containing search results.
     */
    public async lookUpSearchAccounts(params: SearchAccountsParams): Promise<AccountItem[]> {
        return this.datatask.Post(1900, params);
    }

    /**
     * Searches through persons.
     *
     * @datatask 117
     * @datataskName sp_ws_p2_findperson_list
     * @param params
     * @returns promise containing list of persons matchings the search query.
     */
    public searchPersons(params: SearchPersonsParams): Promise<SearchPersonItem[]> {
        return this.datatask.Post(117, params);
    }

    /**
     *
     * @datatask 2772
     * @datataskName sp_ws_p2_ordersearch_typeahead_list
     * @param params
     * @returns
     */
    public searchOrders(params?: SearchOrdersParams): Promise<SearchOrderItem[]> {
        return this.datatask.Post(2772, params);
    }

    /**
     * Retrieves list of products displaying key-name matching the given query.
     *
     * @datatask 3110
     * @datataskName tt_get_product_keyname_2
     * @param params for the search.
     * @returns promise containing list matching the query.
     */
    public searchProductsKeyname(params: SearchProductsKeynameParams): Promise<ProductKeynameItem[]> {
        return this.datatask.Post(3110, params);
    }

    /**
     * @datatask 3411
     * @datataskName tt_get_webpage_keyname
     * @param params the searchtext.
     * @returns promise containing list of webpages matching the searchtext.
     */
    public searchWebpages(params: SearchWebpagesParams): Promise<WebpageSearchItem[]> {
        return this.datatask.Post(3411, params);
    }
}

export interface WebpageSearchItem {
    item_id: string;
    item_name: string;
    webpage_keyname: string;
}

export interface SearchWebpagesParams {
    searchtext: string;
}

export interface SearchProductsKeynameParams {
    searchtext: string;
}

export interface ProductKeynameItem {
    prod_keyname: string;
    prod_id: string;
    prod_name: string;
}

export interface SearchOrderItem {
    order_internal_no: string;
    item_name: string;
    orderby: string;
    cust_no: string;
    cust_name: string;
    project_keyno: string;
    project_name: string;
    order_order_no: string;
    order_offer_no: string;
}

export interface SearchOrdersParams {
    cust_no: string | number;
    searchtext: string;
}

export interface SearchPersonsParams {
    pers_name: string;
}

export interface SearchPersonItem {
    item_id: string;
    item_name: string;
    item_state: string;
    item_parms: string;
}

export interface SearchServiceObjectsParams {
    argtype?: string;
    argvalue?: string;

    /**
     * @default '1'
     */
    show_all?: '1' | '0';
    cust_no?: string | number;
    searchtext: string;
}

export interface SearchAccountsParams {
    searchtext: string;
    businessco_no: string;
    //onlyaccounttype: string; // onlyaccounttype should be added into interface but before that we have to solve following bug issues that appear in CustomerEditComponent and CustProdAccBcsComponent (ODL)?
}

export interface ServiceObjectItem {
    item_id: string;
    item_name: string;
    item_path: string;
    item_parms: string;
    item_name_sub1: string;
}

export interface AccountItem {
    item_id: string;
    item_name: string;
    accounttype: string;
    acvatcode: string;
    orderby: string;
}

export interface InitiatedByPersonNameParams {
    searchtext: string;
    onlyperson: string;
}

export interface InitiatedByPersonNameItem {
    item_id: string;
    item_name: string;
    item_path: string;
    item_state: string;
    item_parms: string;
    item_name_sub1: string;
    orderby: number;
    badgeinfo: string;
    item_glyphicon: string;
    item_glyphicon_2: string;
    item_glyphicon_color: string;
    item_glyphicon_color_2: string;
    item_is_selected: string;
    email: string;
    pkey: string;
}

export interface SearchProjectParams {
    /**
     * The project name to search for.
     */
    project_name: string;

    /**
     * @default 0
     */
    businessco_no?: string | number;

    /**
     * @default 0
     */
    cust_no?: string | number;
}

export interface SearchProjectItem {
    item_id: string;
    item_name: string;
    orderby: string;
}

/**
 * Represents parameters used for searching through customer account.
 */
export interface SearchCustomerAccountInfoParams {
    /**
     * Whether to search through all customers or first the last used ones.
     *
     * @default 'SEARCH'
     */
    lastselect?: 'SEARCH' | 'LAST';

    /**
     * The filter value for the customer to search for.
     */
    cust_name: string;

    /**
     * @default 'ALL'
     */
    custgrp_id?: string;

    /**
     * The customer type for the customer to search for. `'S'` for supplier, `'C'` for customer, `'O'` for neither.
     */
    custtype: 'S' | 'C' | 'O';
}

/**
 * Represents account information for a customer.
 */
export interface CustomerAccountInfo {
    item_id: string;
    item_name: string;
    item_path: string;
    item_state: string;
    item_parms: string;
    orderby: string;
    badgeinfo: string;
    acc_supp_no: string;
    acc_cust_no: string;
    bankaccount: string;
    bic: string;
    iban: string;
}

export interface SearchCustomerParams {
    /**
     * The filter value for the customer to search for.
     */
    cust_name: string;

    /**
     * @default 'ALL'
     */
    custgrp_id?: string;

    /**
     * The customer type for the customer to search for. `'A'` for all, `'S'` for supplier, `'C'` for customer, `'O'` for neither (contacts?) and `'D'` for deliverers.
     */
    custtype?: CustType;

    /**
     * Whether to get to top 50 searches, set as anything other than 'list' to override.
     *
     * @default 'list'
     */
    callfrom?: 'list' | string;

    /**
     * Whether to search through all customers or first the last used ones.
     *
     * @default 'SEARCH'
     */
    lastselect?: 'SEARCH' | 'LAST';
}

export interface CustomerItem extends Item {
    item_id: string;
    item_name: string;
    item_path: string;
    item_state: string;
    item_parms: string;
    orderby: string;
    badgeinfo: string;
}

export interface SearchProductParams {
    prodgrp_id?: 'ALL' | string;
    prod_name: string;
    webpage_name?: string;
    parm1?: '0' | string;
    parm2?: '0' | string;
    supplier_no?: '0' | string;
    search_only_this_supplier?: '0' | string;
    businessco_no?: '0' | string;
    onlyonstock?: '0' | string;
    model_id?: 'ALL' | string;
}

export interface SearchCustactParams {
    /**
     * The search text.
     */
    searchtext: string;

    /**
     * The customer to find custacts for, if any.
     */
    cust_no?: string | number;

    /**
     * Whether to restrict search to custacts of workorder.
     *
     * @default '1'
     */
    restrict_to_workorder?: '1' | '0';
}

export interface ItemCustact {
    item_id: string;
    item_name: string;
    orderby: string;
}

export interface SearchPostPlaceParams {
    /**
     * The query of the search.
     */
    searchtext: string;

    /**
     * Webpagename to add to item_state of the resultset
     */
    webpage_name?: string;

    /**
     * State parms to add to the resultset.
     */
    parm1?: string;
}

export interface PostPlaceItem {
    item_id: string;
    item_name: string;
    item_state: string;
    item_parms: string;
    orderby: `${number}`;
    postplace_id: string;
    postplace_name: string;
    postplace_id_and_name: string;
    country_id: string;
}

export interface SearchStockBatchParams {
    stockbatch_search: string;
    // searchtext?: string;
    stockplace_id: string;
}

export interface StockBatchItem {
    item_id: string;
    item_name: string;
    stock_keyno: string;
    prod_id: string;
    prod_name: string;
    stockbatch_search: string;
}
